import { API_BASE } from '../env'
import { request } from './http'
import type { DownloadedFile } from './files'
import type { Refine, Schema } from './schema'

/**
 * One device in full, as `GET /api/devices/{id}` sends it: the relay's `DeviceWire`, with the
 * connection's transport and the key's protection narrowed to the words the Portal knows.
 *
 * - `available_services`: Service names with their ports attached — `"vnc:5900"`, not `"vnc"`. The
 *   LIST endpoint sends bare names instead; see {@link DeviceListItemWire}. Match these with
 *   `startsWith('vnc:')`, never an exact comparison.
 * - `location`: Nested here, flattened on the list endpoint.
 * - `key_attested`: An Android key attestation chain for the key verified to a Google root.
 * - `auth_mode`: `pop` once the device has registered by proof of possession; a bare token is then
 *   refused.
 */
export type DeviceWire = Refine<
  Schema<'DeviceWire'>,
  {
    /** Present while the agent holds a session with a relay. */
    connection?: {
      server_instance?: string | null
      connected_at?: string | null
      last_heartbeat_at?: string | null
      client_ip?: string | null
      /**
       * How the agent's connection is carried: `"tls"`, `"plain"` (a legacy agent, whose device token
       * crosses the wire in cleartext), or null for a connection recorded before the relay tracked it.
       */
      transport?: DeviceTransport | null
      /** The relay's name for the TLS version — `"Tls13"`; see `formatTlsProtocol`. */
      tls_protocol?: string | null
      tls_cipher?: string | null
    } | null
    /** Where the device's identity key is held (docs/Protocol-Enrollment.md §7). */
    key_protection: KeyProtection | null
  }
>

/**
 * The trust tier of a device's identity key: a TPM, Android StrongBox or Keystore (TEE), a software
 * key file, or no key on record (a legacy device that never reported one).
 */
export type KeyProtection = 'tpm' | 'strongbox' | 'keystore' | 'software' | 'none'

/**
 * Which relay hosts this device's agent.
 *
 * A null `relay_ws_url` means single-server mode, and the viewer derives the socket base from the
 * page origin — the behaviour the Blazor viewers had.
 */
export type DeviceRelayInfoWire = Schema<'DeviceRelayInfoWire'>

// ── Devices ──────────────────────────────────────────────────────────────────────────────────────

export function getDevice(
  deviceId: string,
  token?: string | null,
  signal?: AbortSignal,
): Promise<DeviceWire> {
  return request<DeviceWire>('GET', `${API_BASE}/devices/${deviceId}`, { token, signal })
}

/**
 * One file of the device agent's own log, as an attachment: `file` 0 is the live log and n its n-th
 * rotated file (`<path>.n`, newest first), up to the `keep` that `DeviceWire.agent_log` reports.
 */
export function downloadAgentLog(deviceId: string, file: number): Promise<DownloadedFile> {
  return request<DownloadedFile>('GET', `${API_BASE}/devices/${deviceId}/agent-log?file=${file}`, {
    accept: 'file',
  })
}

/** The name the Support client gives an agent log file: `<device>-relay-agent.log[.n]`. */
export function agentLogFileName(deviceId: string, file: number): string {
  return `${deviceId}-relay-agent.log${file === 0 ? '' : `.${file}`}`
}

export function getDeviceRelayInfo(
  deviceId: string,
  token?: string | null,
): Promise<DeviceRelayInfoWire> {
  return request<DeviceRelayInfoWire>('GET', `${API_BASE}/devices/${deviceId}/relay`, { token })
}

/**
 * One device as the LIST endpoint describes it.
 *
 * Deliberately a separate type from {@link DeviceWire}, because `/api/devices` and
 * `/api/devices/{id}` do not describe a device the same way. The list flattens location and brand
 * into `location_name` / `brand_name`, and its `services` carries **bare** names (`"vnc"`) where the
 * detail endpoint's `available_services` carries **prefixed** ones (`"vnc:5900"`). One interface for
 * both would make the service gating wrong on one page or the other: an exact `includes('vnc')`
 * against a prefixed list matches nothing, and `startsWith('vnc:')` against a bare list likewise.
 *
 * - `services`: Bare service names — see the note above.
 * - `link`: How the current connection is carried; null when offline or not known.
 */
export type DeviceListItemWire = Refine<
  Schema<'DeviceListItemWire'>,
  {
    key_protection: KeyProtection | null
  }
>

export type DeviceTransport = 'tls' | 'plain'

/**
 * The list endpoint's account of an online agent's connection.
 *
 * - `tls_protocol`: `"Tls13"`, `"Tls12"` — the relay's spelling; see `formatTlsProtocol`.
 */
export type DeviceLinkWire = Schema<'DeviceLinkWire'>

/**
 * - `total`: What the relay actually sends. It was declared as `total_count` and `total_pages`, and
 *   the relay sends neither — `ApiHttpServer` answers `{ devices, total, page, limit, has_more }`.
 *   Both phantom fields read `undefined` at run time, which had consequences nothing caught. The
 *   Devices page computed `totalPages = list?.total_pages ?? 0` and then rendered its pager only
 *   when `totalPages > 1` — always false — so **the pagination footer never appeared on any fleet
 *   of any size**, and past the first 25 devices there was no way to reach page two. The System
 *   Logs device filter read `total_count ?? 0` and so has always reported zero devices. Invisible
 *   to the type checker, the linter and the suite alike, because the fixtures were written from the
 *   same declaration: they agreed with the mistake. The same shape as the `/ml/trends` crash — a
 *   wire type transcribed from a DTO rather than from a response.
 */
export type DeviceListWire = Refine<
  Schema<'DeviceListWire'>,
  {
    devices: DeviceListItemWire[]
  }
>

export type BrandWire = Schema<'BrandWire'>

/**
 * A store. `timezone` is its IANA id (its maintenance windows and business hours are read in it);
 * `store_type_id` is a `StoreTypeWire` id, absent when the store has none. Rule groups select on it.
 */
export type LocationWire = Schema<'LocationWire'>

/**
 * A device type.
 *
 * **There is deliberately no `type` field.** `DeviceTypes.razor` renders one, validates it as
 * required on save, and the Blazor client model declares it — but `device_types` has no such
 * column, the server model has no such property, its projection cannot emit one, and neither the
 * create nor the update request accepts it. That column has always been blank, and every save has
 * demanded a value the server discards. (Router-only behaviour follows the platform the agent
 * reports, not the device type.)
 *
 * The trailing fields are optional because the device-details page reads only `id` and `name`,
 * while the administration page shows the rest.
 */
export type DeviceTypeWire = Schema<'DeviceType'>

/** The filters the list endpoint understands. Omitted values are simply not sent. */
export interface DeviceListQuery {
  page: number
  limit: number
  search?: string
  online?: boolean
  brandId?: string
  locationId?: string
  deviceTypeId?: string
  /** Only devices connected over TLS (`true`) or plain TCP (`false`). Implies online. */
  encrypted?: boolean
  /** Only devices whose identity key is held there. */
  keyProtection?: KeyProtection
  /** status, name, brand, location, type or last_seen; sorted by the relay across every page. */
  sort?: string
  order?: 'asc' | 'desc'
}

export function getBrands(): Promise<Schema<'BrandListWire'>> {
  return request<Schema<'BrandListWire'>>('GET', `${API_BASE}/brands`)
}

export function getLocations(): Promise<{ locations: LocationWire[] }> {
  return request<{ locations: LocationWire[] }>('GET', `${API_BASE}/locations`)
}

export function getDeviceTypes(): Promise<Schema<'DeviceTypeListWire'>> {
  return request<Schema<'DeviceTypeListWire'>>('GET', `${API_BASE}/device-types`)
}

export function listDevices(query: DeviceListQuery, signal?: AbortSignal): Promise<DeviceListWire> {
  const params = new URLSearchParams({
    page: String(query.page),
    limit: String(query.limit),
  })
  // Each filter is added only when set. An empty `search=` is not the same request as no search at
  // all — the server treats a present-but-empty value as a filter matching nothing — and `online`
  // is tested against undefined rather than for truthiness, because `false` is a real filter.
  if (query.search) params.set('search', query.search)
  if (query.online !== undefined) params.set('online', String(query.online))
  if (query.brandId) params.set('brand', query.brandId)
  if (query.locationId) params.set('location', query.locationId)
  if (query.deviceTypeId) params.set('device_type', query.deviceTypeId)
  if (query.encrypted !== undefined) params.set('encrypted', String(query.encrypted))
  if (query.keyProtection) params.set('key_protection', query.keyProtection)
  if (query.sort) {
    params.set('sort', query.sort)
    params.set('order', query.order ?? 'asc')
  }

  return request<DeviceListWire>('GET', `${API_BASE}/devices?${params.toString()}`, { signal })
}

/**
 * Reboots a device.
 *
 * No body. The Blazor client sent an empty string with a JSON content type — which is not valid
 * JSON, and was tolerated only because the handler never reads the body.
 */
export function rebootDevice(deviceId: string): Promise<Schema<'SuccessMessageWire'>> {
  return request<Schema<'SuccessMessageWire'>>('POST', `${API_BASE}/devices/${deviceId}/reboot`)
}

/**
 * The acts a technician takes on a device, beside Reboot: ping it, lock its screen, show a message
 * on it, wipe it.
 *
 * Each answers as soon as the command is on its way, not when the device has done it — a register can
 * be asleep, and holding a web request open until it answers would hang the page. How it went
 * arrives in the device's activity, keyed by the `request_id` this returns
 * (Plans/esper-ux-parity.md, Phase 2).
 */
export type DeviceAction = 'ping' | 'lock' | 'message' | 'factory-reset'

type Accepted = Promise<Schema<'DeviceCommandAcceptedWire'>>

/*
 * One function per act, each with its path written out.
 *
 * A single call with `${action}` in the path would be shorter, and the route inventory could not
 * read it: it checks every call the portal makes against the routes the relay serves, and a path
 * assembled from a variable is a hole it cannot fill, so all four would go unchecked.
 */
function pingDevice(deviceId: string): Accepted {
  return request<Schema<'DeviceCommandAcceptedWire'>>('POST', `${API_BASE}/devices/${deviceId}/ping`)
}

function lockDevice(deviceId: string): Accepted {
  return request<Schema<'DeviceCommandAcceptedWire'>>('POST', `${API_BASE}/devices/${deviceId}/lock`)
}

function messageDevice(deviceId: string, text: string): Accepted {
  return request<Schema<'DeviceCommandAcceptedWire'>>('POST', `${API_BASE}/devices/${deviceId}/message`, {
    json: { text },
  })
}

function factoryResetDevice(deviceId: string): Accepted {
  return request<Schema<'DeviceCommandAcceptedWire'>>('POST', `${API_BASE}/devices/${deviceId}/factory-reset`)
}

export function sendDeviceAction(
  deviceId: string,
  action: DeviceAction,
  body?: { text: string },
): Accepted {
  switch (action) {
    case 'ping':
      return pingDevice(deviceId)
    case 'lock':
      return lockDevice(deviceId)
    case 'message':
      return messageDevice(deviceId, body?.text ?? '')
    case 'factory-reset':
      return factoryResetDevice(deviceId)
  }
}

// ── Device administration ────────────────────────────────────────────────────────────────────────

/** What `/admin/devices/{id}` returns. Only the device type is read from it. */
export type AdminDeviceWire = Schema<'AdminDeviceWire'>

export interface AdminDeviceUpdateWire {
  name: string | null
  description: string | null
  /**
   * The device's CURRENT enabled state, always sent.
   *
   * **Not nullable, and an earlier version of this type got that wrong.** It sent `null` on the
   * reasoning that no control changes this, so no value should be expressed — but the server
   * applies `body.IsEnabled ?? true`, so `null` does not mean "leave it alone", it means **enable
   * it**. Omitting the field silently re-enables a disabled device every time somebody renames it.
   * The Blazor page has the same defect; both call sites now send what the device already is.
   */
  is_enabled: boolean
  device_type_id: string
}

export function adminGetDeviceTypes(): Promise<Schema<'AdminDeviceTypeListWire'>> {
  return request<Schema<'AdminDeviceTypeListWire'>>('GET', `${API_BASE}/admin/device-types`)
}

export function adminGetDevice(deviceId: string): Promise<AdminDeviceWire> {
  return request<AdminDeviceWire>('GET', `${API_BASE}/admin/devices/${deviceId}`)
}

export function adminUpdateDevice(
  deviceId: string,
  body: AdminDeviceUpdateWire,
): Promise<Schema<'AdminMessageWire'>> {
  return request<Schema<'AdminMessageWire'>>('PUT', `${API_BASE}/admin/devices/${deviceId}`, {
    json: body,
  })
}

export function adminDeleteDevice(deviceId: string): Promise<Schema<'AdminMessageWire'>> {
  return request<Schema<'AdminMessageWire'>>('DELETE', `${API_BASE}/admin/devices/${deviceId}`)
}

// ── Kept screens and collected logs (Plans/esper-ux-parity.md, Phase 6) ──────────────────────────

export type DeviceScreenshotWire = Schema<'DeviceScreenshotWire'>
export type DeviceLogBundleWire = Schema<'DeviceLogBundleWire'>

/** The screens kept for a device, newest first. */
export function getScreenshots(deviceId: string): Promise<Schema<'DeviceScreenshotsWire'>> {
  return request<Schema<'DeviceScreenshotsWire'>>('GET', `${API_BASE}/devices/${deviceId}/screenshots`)
}

/**
 * Keeps a screen grabbed from a live session.
 *
 * The PNG is the body, not a field in a JSON object: a screen is about a megabyte, and base64 would
 * make it a third bigger to say the same thing.
 */
export function saveScreenshot(deviceId: string, png: Blob): Promise<DeviceScreenshotWire> {
  // No width or height: the relay reads them out of the PNG, which cannot disagree with itself.
  return request<DeviceScreenshotWire>('POST', `${API_BASE}/devices/${deviceId}/screenshots`, {
    binary: png,
  })
}

export function deleteScreenshot(deviceId: string, screenshotId: string): Promise<Schema<'SuccessMessageWire'>> {
  return request<Schema<'SuccessMessageWire'>>(
    'DELETE',
    `${API_BASE}/devices/${deviceId}/screenshots/${screenshotId}`,
  )
}

/**
 * One kept screen, as bytes.
 *
 * **Fetched, not linked.** This endpoint wants an `Authorization` header, and neither `<img src>`
 * nor `<a href>` sends one — so a tile painted a broken image and pressing it showed the relay's
 * 401 as text. A token in the query string is the other way to make a bare URL work, and it is the
 * trade `SessionsPage` refuses by name: it writes a live bearer token into browser history, the
 * relay's access log and every proxy between. The caller mints an object URL from the blob instead,
 * and revokes it.
 */
export function getScreenshotFile(
  deviceId: string,
  screenshotId: string,
  /**
   * The gallery's small copy. The relay serves the full image for a screenshot that has none, so a
   * caller never has to ask which it will get — and asking for the thumbnail is deliberately NOT
   * recorded as looking at the screenshot: a gallery paints several tiles per visit.
   */
  size?: 'thumb',
): Promise<DownloadedFile> {
  // Two literal paths rather than one with an interpolated query, as the device commands are: the
  // portal route inventory matches these against the server's routes, and it cannot resolve a hole
  // it has no specimen value for.
  return size === 'thumb'
    ? request<DownloadedFile>(
        'GET',
        `${API_BASE}/devices/${deviceId}/screenshots/${screenshotId}?size=thumb`,
        { accept: 'file' },
      )
    : request<DownloadedFile>(
        'GET',
        `${API_BASE}/devices/${deviceId}/screenshots/${screenshotId}`,
        { accept: 'file' },
      )
}

/**
 * Keeps the gallery's small copy of a screenshot already stored.
 *
 * A second call rather than a field of the first: the capture must be kept whether or not a
 * thumbnail can be made, so the screenshot exists before this is attempted (migration 0100).
 */
export function saveScreenshotThumbnail(
  deviceId: string,
  screenshotId: string,
  png: Blob,
): Promise<Schema<'SuccessMessageWire'>> {
  return request<Schema<'SuccessMessageWire'>>(
    'POST',
    `${API_BASE}/devices/${deviceId}/screenshots/${screenshotId}/thumbnail`,
    { binary: png },
  )
}

export function getLogBundles(deviceId: string): Promise<Schema<'DeviceLogBundlesWire'>> {
  return request<Schema<'DeviceLogBundlesWire'>>('GET', `${API_BASE}/devices/${deviceId}/log-bundles`)
}

/** Asks the device for its logs. Answers at once; watch the bundle's status for the rest. */
export function collectLogs(deviceId: string): Promise<DeviceLogBundleWire> {
  return request<DeviceLogBundleWire>('POST', `${API_BASE}/devices/${deviceId}/log-bundles`)
}

/** One collected bundle, as bytes. Fetched rather than linked, for {@link getScreenshotFile}'s reason. */
export function getLogBundleFile(deviceId: string, bundleId: string): Promise<DownloadedFile> {
  return request<DownloadedFile>(
    'GET',
    `${API_BASE}/devices/${deviceId}/log-bundles/${bundleId}/download`,
    { accept: 'file' },
  )
}
